import React, { useState, useRef, useEffect, useCallback } from 'react';
import { trackAudioPlayerStart, trackAudioPlayerPause, trackAudioPlayerSeek } from '../utils/analytics';

interface AudioPlayerProps {
  src: string;
  title?: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ 
  src, 
  title = "Listen to our AI Screening a Candidate" 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioData, setAudioData] = useState<number[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragTime, setDragTime] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const audioRef = useRef<HTMLAudioElement>(null);
  const animationRef = useRef<number | undefined>(undefined);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Generate static waveform pattern for loading state
  const generateStaticWaveform = useCallback(() => {
    const bars = 200;
    // Create a more realistic static pattern with variation
    const staticData = Array.from({ length: bars }, (_, i) => {
      // Create a wave-like pattern with some randomness
      const wave = Math.sin(i * 0.1) * 0.3 + 0.5;
      const random = Math.random() * 0.2;
      return Math.max(0.15, Math.min(0.95, wave + random));
    });
    return staticData;
  }, []);

  // Initialize static waveform immediately on mount
  useEffect(() => {
    setAudioData(generateStaticWaveform());
  }, [generateStaticWaveform]);

  // Generate real waveform data from audio file
  useEffect(() => {
    const generateWaveformFromAudio = async () => {
      setIsLoading(true);
      // Show static waveform while loading
      setAudioData(generateStaticWaveform());
      
      try {
        const response = await fetch(src);
        const arrayBuffer = await response.arrayBuffer();
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        
        const rawData = audioBuffer.getChannelData(0); // Get first channel
        const samples = 200; // Much more granular - 200 bars for smoother animation
        const blockSize = Math.floor(rawData.length / samples);
        const filteredData = [];
        
        for (let i = 0; i < samples; i++) {
          let blockStart = blockSize * i;
          let sum = 0;
          for (let j = 0; j < blockSize; j++) {
            sum += Math.abs(rawData[blockStart + j]);
          }
          filteredData.push(sum / blockSize);
        }
        
        // Normalize the data
        const maxVal = Math.max(...filteredData);
        const normalizedData = filteredData.map(val => (val / maxVal) * 0.8 + 0.1);
        
        setAudioData(normalizedData);
        setIsLoading(false);
      } catch (error) {
        console.log('Could not generate waveform, using fallback');
        // Keep static waveform if audio analysis fails
        setIsLoading(false);
      }
    };

    if (src) {
      generateWaveformFromAudio();
    }
  }, [src, generateStaticWaveform]);

  // Smooth animation loop for real-time progress updates
  useEffect(() => {
    const updateProgress = () => {
      const audio = audioRef.current;
      if (audio && isPlaying) {
        setCurrentTime(audio.currentTime);
        animationRef.current = requestAnimationFrame(updateProgress);
      }
    };

    if (isPlaying) {
      animationRef.current = requestAnimationFrame(updateProgress);
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const setAudioDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      setHasStarted(false); // Reset for potential replay
    };

    audio.addEventListener('loadedmetadata', setAudioDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', setAudioDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      // Pausing the audio
      audio.pause();
      trackAudioPlayerPause(title, src, currentTime, duration);
    } else {
      // Starting/resuming the audio
      audio.play();
      
      // Track start event only on first play
      if (!hasStarted) {
        trackAudioPlayerStart(title, src);
        setHasStarted(true);
      }
    }
    setIsPlaying(!isPlaying);
  };

  const getTimeFromPosition = (clientX: number) => {
    const canvas = canvasRef.current;
    if (!canvas || duration === 0) return 0;

    const rect = canvas.getBoundingClientRect();
    const clickX = clientX - rect.left;
    const canvasWidth = rect.width;
    
    // Calculate the percentage and clamp between 0 and 1
    const clickPercentage = Math.max(0, Math.min(1, clickX / canvasWidth));
    
    return clickPercentage * duration;
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (duration === 0) return;
    
    setIsDragging(true);
    const newTime = getTimeFromPosition(event.clientX);
    setDragTime(newTime);
    
    // Prevent text selection while dragging
    event.preventDefault();
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging || duration === 0) return;
    
    const newTime = getTimeFromPosition(event.clientX);
    setDragTime(newTime);
  };

  const handleMouseUp = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging) return;
    
    const audio = audioRef.current;
    if (!audio) return;
    
    const previousTime = currentTime;
    const newTime = getTimeFromPosition(event.clientX);
    
    // Track seek event
    trackAudioPlayerSeek(title, src, previousTime, newTime, duration);
    
    audio.currentTime = newTime;
    setCurrentTime(newTime);
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      const audio = audioRef.current;
      if (audio) {
        const previousTime = currentTime;
        
        // Track seek event
        trackAudioPlayerSeek(title, src, previousTime, dragTime, duration);
        
        audio.currentTime = dragTime;
        setCurrentTime(dragTime);
      }
      setIsDragging(false);
    }
  };

  // Touch event handlers for mobile
  const handleTouchStart = (event: React.TouchEvent<HTMLCanvasElement>) => {
    if (duration === 0) return;
    
    event.preventDefault(); // Prevent scrolling
    const touch = event.touches[0];
    setIsDragging(true);
    const newTime = getTimeFromPosition(touch.clientX);
    setDragTime(newTime);
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDragging || duration === 0) return;
    
    event.preventDefault(); // Prevent scrolling
    const touch = event.touches[0];
    const newTime = getTimeFromPosition(touch.clientX);
    setDragTime(newTime);
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDragging) return;
    
    event.preventDefault();
    const audio = audioRef.current;
    if (!audio) return;
    
    const touch = event.changedTouches[0];
    const previousTime = currentTime;
    const newTime = getTimeFromPosition(touch.clientX);
    
    // Track seek event
    trackAudioPlayerSeek(title, src, previousTime, newTime, duration);
    
    audio.currentTime = newTime;
    setCurrentTime(newTime);
    setIsDragging(false);
  };


  // Use drag time when dragging, otherwise use current time
  const displayTime = isDragging ? dragTime : currentTime;
  const progress = duration > 0 ? (displayTime / duration) * 100 : 0;

  // Canvas drawing function
  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || audioData.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height } = canvas;
    const barWidth = 2;
    const barSpacing = 1;
    const totalBarWidth = barWidth + barSpacing;
    const numBars = Math.floor(width / totalBarWidth);
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Calculate cursor position (only show if audio is loaded and not in loading state)
    const cursorX = (!isLoading && duration > 0) ? (progress / 100) * width : 0;

    // Draw waveform bars
    for (let i = 0; i < numBars && i < audioData.length; i++) {
      const x = i * totalBarWidth;
      const barHeight = Math.max(6, audioData[i] * height * 0.8);
      const y = (height - barHeight) / 2;

      // For loading state, show all bars in gray
      // For loaded state, determine if this bar is before the cursor
      const isActive = isLoading ? false : x < cursorX;
      
      // Set color based on cursor position or loading state
      ctx.fillStyle = isLoading ? '#d1d5db' : (isActive ? 'rgba(59, 130, 246, 0.8)' : '#d1d5db');
      
      // Draw rounded rectangle (bar)
      ctx.beginPath();
      ctx.roundRect(x, y, barWidth, barHeight, 1);
      ctx.fill();
    }

    // Draw cursor line - show during playback or dragging (only when loaded)
    if (!isLoading && (isPlaying || isDragging) && progress > 0 && duration > 0) {
      ctx.strokeStyle = isDragging ? 'rgba(59, 130, 246, 0.9)' : 'rgba(59, 130, 246, 1)';
      ctx.lineWidth = isDragging ? 3 : 2;
      ctx.beginPath();
      ctx.moveTo(cursorX, 0);
      ctx.lineTo(cursorX, height);
      ctx.stroke();
      
      // Add a draggable handle at the top when dragging
      if (isDragging) {
        ctx.fillStyle = 'rgba(59, 130, 246, 1)';
        ctx.beginPath();
        ctx.arc(cursorX, 8, 6, 0, 2 * Math.PI);
        ctx.fill();
        
        // Add white center to the handle
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.arc(cursorX, 8, 3, 0, 2 * Math.PI);
        ctx.fill();
      }
    }
  }, [audioData, progress, isPlaying, isDragging, isLoading, duration]);

  // Draw waveform whenever data or progress changes
  useEffect(() => {
    drawWaveform();
  }, [drawWaveform]);

  return (
    <div className="animate-fadeInUp" style={{ animationDelay: '0.5s' }}>
      <div className="max-w-lg mx-auto">
        {/* Audio Player Container - Integrated Design */}
        <div className="p-6 rounded-2xl border-2 border-white/20 bg-[#ffffff1a] backdrop-blur-sm transition-all duration-200 ease-out hover:border-white/30 hover:bg-[#ffffff22]">
          {/* Title - Integrated */}
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/10">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-white flex-1">{title}</p>
            {isPlaying && (
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 bg-primary-400 rounded-full animate-pulse"></div>
                <div className="w-1.5 h-1.5 bg-primary-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-1.5 h-1.5 bg-primary-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
              </div>
            )}
          </div>

          {/* Audio Player Controls */}
          <div className="flex items-center gap-4">
            {/* Play Button - Perfectly Circular */}
            <button
              onClick={togglePlayPause}
              className="w-16 h-16 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex-shrink-0 animate-pulsate"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <svg className="w-8 h-8 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                </svg>
              ) : (
                <svg className="w-8 h-8 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
            </button>

            {/* Canvas Waveform - Aligned with Button */}
            <div className="flex-1">
              <canvas
                ref={canvasRef}
                width={400}
                height={64}
                className={`w-full h-16 ${isDragging ? 'cursor-grabbing' : 'cursor-pointer'} touch-none`}
                style={{ imageRendering: 'pixelated', touchAction: 'none' }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              />
            </div>
          </div>
        </div>

        {/* Hidden Audio Element */}
        <audio ref={audioRef} src={src} preload="metadata" />
      </div>
    </div>
  );
};

export default AudioPlayer;
